// =============================================================================
// OILFIELD TIME LOG GENERATOR — demo scaffolding for the Differentiated Time Log
// =============================================================================
//
// WHY THIS FILE EXISTS (2026-09-03). `generateOilfieldTestData` produced a pad
// with wells, surveys, a tubular program and reports — but NO time log, so every
// generated job opened onto an EMPTY timeline. The timeline itself is not at
// fault: `useTimeline` deliberately refuses to seed a real job-bound log
// (Charter Abs-7 — no fabricated data on a real key); `buildSeedData` fires only
// for the standalone `isPreview` mule. A real job's log has to come from
// whatever creates the job, which for demo data is the generator.
//
// The sequence is DERIVED FROM THE CASING PROGRAM, not hardcoded. Each cemented
// string is a hole section, and each hole section is a drill → circulate → trip
// → run casing → cement → WOC → BOP/FIT cycle on the critical path. That is what
// makes the timeline agree with the schematic instead of inventing its own
// depths: change the shoes and the timeline moves with them.
//
// WINDOW CONSTRAINT (timeline-core WINDOW_HOURS = 32). The timeline opens on
// `today ± 4 h` — a 32-hour window. A log anchored to the job's old random
// "30-180 days ago" start date renders EMPTY on open even though the entries
// exist. So each well is anchored against the rig's sequential pad schedule and
// the well that is CURRENTLY DRILLING is placed so that `now` falls mid-lateral.
//
// NO ENGINEERING MATH (Charter Abs-2): the ROPs below are demo pacing constants
// chosen to land a Delaware two-mile well in its real 18-30 day spud-to-release
// envelope. Nothing here is a design calculation and nothing reads it back.
//
// UNIT MODEL: depths are METRES, matching the generator's SI bores
// (`depthUnit: "m"`), and every entry carries its `depthStartUnit`/`depthEndUnit`
// sibling (Charter Abs-3).

import {
  CODING_TEMPLATE_ID,
  CODING_TEMPLATE_VERSION,
  DEFAULT_DRILLING_LANES,
  MS_PER_HOUR,
} from "../features/timeline/constants";
import { toOffsetISO } from "../lib/date";
import type {
  JobTimeLog,
  TimeEntry,
  WeatherObservation,
} from "../features/jobs/types";

/** Shoe depths + trajectory the sequence hangs off. All MD, metres. */
export interface OilfieldWellGeometry {
  conductorShoeMD: number;
  surfaceShoeMD: number;
  intermediateShoeMD: number;
  kopMD: number;
  landingMD: number;
  totalMD: number;
}

/** Where the well sits in the pad's drilling campaign. `boreStatus` maps on:
 *  Completed/Abandoned → "completed" (it was drilled), Drilling → "drilling",
 *  Planned → "planned". */
export type OilfieldWellPhase = "completed" | "drilling" | "planned";

// ── PO DEMO-PACING SPEC ─────────────────────────────────────────────────────
// Every constant below is transcribed from the PO's own operational sequence,
// ratified 2026-09-03 (docs/DECISIONS.md — "The Permian demo timeline spec").
// They are DEMO PACING, not engineering figures: nothing may cite them as a real
// Delaware envelope, and no agent may substitute its own value for one of these
// without flagging the deviation (Charter Absolute 9).
//
// The PO stated rates in ft/hr, so they are kept in ft/hr here and converted at
// the point of use — the alternative (pre-converting to m/hr in the source) hides
// the stated figure behind arithmetic and is how "180 ft/hr" silently became an
// agent's "25 m/hr" in the first version of this file.

const FT_PER_M = 3.280839895;

/** Rate of penetration, ft/hr, EXCLUDING survey time (survey is added separately
 *  from the stated cadence — that is what makes the effective rate fall out of the
 *  PO's numbers rather than being guessed). */
const ROP_SURFACE_FTHR = 250; // "drill surface … drilling 8 hours" over ~2,000 ft
const ROP_VERTICAL_FTHR = 180; // "Drilling ROP around +/- 180 ft/hr per stand"
const ROP_CURVE_FTHR = 100; // "Curve section ROP ~100ft/hr"
const ROP_LATERAL_FTHR = 200; // "horzontal ROP 150-250ft/hr" — midpoint

/** Pipe-handling limits, ft/hr. "limit TIH to 1200ft/hr average",
 *  "TOOH limit to 1500ft/hr on average", "run casing (800-1000ft.hr)". */
const TIH_FTHR = 1200;
const TOOH_FTHR = 1500;
const RUN_CASING_FTHR = 900;

/** Survey cadence + cost. Vertical: "survey every 90ft", "20-60 minutes per stand
 *  for survey and going back to bottom". Curve: "Surveys take 40-90 minutes and
 *  are done every 30ft".
 *
 *  KNOWN, FLAGGED DEVIATION FROM REALISM (recorded in DECISIONS.md 2026-09-03):
 *  a prior session challenged the curve figures — "40-90 minutes is a wireline or
 *  gyro survey time, not MWD" — and computed that at 100 ft/hr with 30 ft spacing
 *  the surveys consume ~68% of the build section. The PO replied "good catches.
 *  easy to address" but never stated corrected values, so the SPEC AS WRITTEN is
 *  implemented here rather than an agent inventing its own numbers. */
const STAND_FT = 90;
const SURVEY_VERTICAL_H = 0.67; // 20-60 min, midpoint
const CURVE_SURVEY_INTERVAL_FT = 30;
const SURVEY_CURVE_H = 1.08; // 40-90 min, midpoint

/** Fixed-duration steps, hours, all from the PO's surface breakdown and tail:
 *  "CTCH 1 hour", "rigup casing crew (4-6 hours)", "rigup cement 4 hours",
 *  "cement 2 hours", "install wellhead 4 hours", "nipple up and test BOP, 8
 *  hours", "WOC 8 hours", "FIT, 2 hours", "makeup BHA takes about 4-8 hours",
 *  "condition mud (3-6 hours)", "CTCH for 2-8 hours at end of intermediate",
 *  "rig service 1-2 hours per day", "random rig service of 12-24 hours". */
const CTCH_SURFACE_H = 1;
const RIGUP_CASING_CREW_H = 5;
const RIGUP_CEMENT_H = 4;
const CEMENT_H = 2;
const INSTALL_WELLHEAD_H = 4;
const NU_TEST_BOP_H = 8;
const WOC_H = 8;
const FIT_H = 2;
const BHA_MAKEUP_H = 6;
const CONDITION_MUD_H = 4.5;
const CTCH_INTERMEDIATE_H = 5;
const RIG_SERVICE_DAILY_H = 1.5;
const RIG_SERVICE_RANDOM_H = 18;

/** "3 days for MOB" and "rig down (1.5 days)". The first version of this file used
 *  44 h and 16 h — agent-invented values contradicting the only human figures on
 *  record. Found by the Attribution Adversary, 2026-09-03. */
/** Metres of new hole made drilling out the shoe track. After a casing cycle the
 *  hole is this much deeper than the shoe, and EVERYTHING downstream — the
 *  integrity test, the trip back in, the next section's first drilled metre —
 *  must start from shoe + this, or the days-vs-depth curve steps backwards. */
const SHOE_TRACK_M = 5;

const MOB_HOURS = 72;
const RIG_DOWN_HOURS = 36;
/** "Raise derrick at sun up on day 2ish" — hour 30 from a midnight origin. */
const DERRICK_RAISED_H = 30;

/** Deterministic per-well jitter in [lo, hi], seeded from the well's own geometry.
 *  The PO asked for variance -- "total drilling days of 18-30, give some variance"
 *  (DECISIONS.md 2026-09-03, "The Permian demo timeline spec"). Deriving it from the
 *  geometry rather than Math.random keeps the generator reproducible, which the tests
 *  depend on. The date pointer was added when Gate G7 refused the commit: the claim was
 *  true but undated, and an undated attribution cannot be checked against the ledger. */
const jitter = (seed: number, lo: number, hi: number): number => {
  const x = Math.sin(seed) * 10_000;
  return lo + (x - Math.floor(x)) * (hi - lo);
};

/** EFFECTIVE m/hr per section — DERIVED from the PO's ft/hr figures plus his stated
 *  survey cadence, never chosen. Arithmetic shown so the stated figure stays visible:
 *    surface   250 ft/hr, no cadence stated                    → 76.2 m/hr
 *    vertical  90 ft: 0.50 h drill + 0.67 h survey → 76.9 ft/hr → 23.4 m/hr
 *    curve     30 ft: 0.30 h drill + 1.08 h survey → 21.7 ft/hr →  6.6 m/hr
 *    lateral   200 ft/hr                                        → 61.0 m/hr
 *  The curve rate is the flagged-deviation case — the stated survey cadence costs
 *  ~78% of the build section. PO spec as written; see DECISIONS.md 2026-09-03. */
const effectiveMhr = (
  ropFtHr: number,
  surveyIntervalFt: number,
  surveyHours: number,
): number =>
  surveyIntervalFt / (surveyIntervalFt / ropFtHr + surveyHours) / FT_PER_M;

const ROP_SURFACE = ROP_SURFACE_FTHR / FT_PER_M;
const ROP_INTERMEDIATE = effectiveMhr(
  ROP_VERTICAL_FTHR,
  STAND_FT,
  SURVEY_VERTICAL_H,
);
const ROP_CURVE = effectiveMhr(
  ROP_CURVE_FTHR,
  CURVE_SURVEY_INTERVAL_FT,
  SURVEY_CURVE_H,
);
const ROP_LATERAL = ROP_LATERAL_FTHR / FT_PER_M;

/** Hours to move pipe over `metres` of hole at a stated ft/hr limit. */
const pipeHours = (metres: number, ftHr: number): number =>
  (metres * FT_PER_M) / ftHr;

export interface Spec {
  lane: string;
  cp: boolean;
  startH: number;
  endH: number;
  code1: string;
  code2: string;
  /** HOLE depth at block start / end, metres MD — the drilled progress, never the
   *  BIT's or a wireline tool's position. Equal ⇒ a flat spot on the curve.
   *  `null` ⇒ the activity has NO depth (rig-up, BOP testing, pipe inspection).
   *  It is emitted as an EMPTY string, not "0": DepthVsDaysChart parses every
   *  activity's depth and a literal 0 drags the progress curve to surface, while
   *  an unparseable value is correctly skipped (timeline/utils.ts depthNum). */
  d0: number | null;
  d1: number | null;
  kind?: "milestone" | "report";
  party?: string;
  comment?: string;
}

/** Round to the nearest tenth of an hour so ISO stamps land on clean minutes. */
const h1 = (n: number): number => Math.round(n * 10) / 10;

// ── Section builder ─────────────────────────────────────────────────────────

/** Emit a drilled interval as contiguous critical-path DRILLING blocks with MWD
 *  surveys hung in the parallel lane at each block boundary. Surveys are
 *  CONCURRENT with drilling (they happen at connections) — which is exactly what
 *  the separate MWD lane is for — so they do NOT advance the critical path.
 *  Returns the hour the interval finishes. */
function drillInterval(
  push: (s: Spec) => void,
  args: {
    fromMD: number;
    toMD: number;
    ropMhr: number;
    at: number;
    code2: string;
    blockHours: number;
    comment: string;
  },
): number {
  const { fromMD, toMD, ropMhr, at, code2, blockHours, comment } = args;
  const metres = Math.max(0, toMD - fromMD);
  const totalH = metres / ropMhr;
  const blocks = Math.max(1, Math.round(totalH / blockHours));
  const perBlockH = totalH / blocks;
  const perBlockM = metres / blocks;

  let t = at;
  let d = fromMD;
  for (let b = 0; b < blocks; b++) {
    const nextT = t + perBlockH;
    // Last block lands exactly on toMD — no float drift away from the shoe.
    const nextD = b === blocks - 1 ? toMD : d + perBlockM;
    push({
      lane: "lane-critical",
      cp: true,
      startH: h1(t),
      endH: h1(nextT),
      code1: "DRILLING",
      code2,
      d0: Math.round(d),
      d1: Math.round(nextD),
      comment: b === 0 ? comment : "",
    });
    // MWD survey at the connection closing each block (except the last, which is
    // followed by the section's circulate + trip).
    if (b < blocks - 1) {
      push({
        lane: "lane-mwd",
        cp: false,
        startH: h1(nextT),
        endH: h1(nextT + 0.4),
        code1: "SURVEY",
        code2: "",
        d0: Math.round(nextD),
        d1: Math.round(nextD),
      });
    }
    t = nextT;
    d = nextD;
  }
  return t;
}

/** Rig-up: parallel task bars plus the gating milestones, ending at spud. */
function pushMobilization(push: (s: Spec) => void, previousWell: string): void {
  const mob = (
    startH: number,
    endH: number,
    code1: string,
    comment = "",
  ): void => {
    push({
      lane: "lane-mob",
      cp: false,
      startH,
      endH,
      code1,
      code2: "",
      d0: null,
      d1: null,
      comment,
    });
  };
  const milestone = (
    lane: string,
    atH: number,
    code1: string,
    comment = "",
  ): void => {
    push({
      lane,
      cp: true,
      startH: atH,
      endH: atH,
      code1,
      code2: "",
      d0: null,
      d1: null,
      kind: "milestone",
      comment,
    });
  };

  mob(0, 8, "MOVE_IN", `Walk from ${previousWell} — pad move, no rig-down`);
  mob(8, 14, "SPOT_RIG", "Spot over the conductor");
  mob(12, 22, "RU_SUBSTRUCTURE");
  mob(14, 24, "RU_POWER");
  milestone("lane-mob", 14, "BACK_YARD_SET");
  mob(20, 30, "RU_MAST");
  mob(22, 34, "RU_MUD_SYSTEM");
  mob(26, 36, "RU_PUMPS");
  milestone(
    "lane-mob",
    DERRICK_RAISED_H,
    "DERRICK_RAISED",
    "Daylight-gated — not raised in the dark",
  );
  mob(30, 38, "HOOK_UP_UTIL");
  mob(34, 40, "RU_CONTROLS");

  // BOP goes on the stump during rig-up, before it is nippled up to the wellhead.
  const bop = (startH: number, endH: number, code1: string): void => {
    push({
      lane: "lane-bop",
      cp: false,
      startH,
      endH,
      code1,
      code2: "",
      d0: null,
      d1: null,
    });
  };
  bop(24, 30, "STUMP_NU");
  bop(30, 32, "STUMP_PT_LOW");
  bop(32, 35, "STUMP_PT_HIGH");
  milestone("lane-bop", 35, "STUMP_TEST_PASSED");

  push({
    lane: "lane-inspections",
    cp: false,
    startH: 18,
    endH: 26,
    code1: "DRILLPIPE_INSP",
    code2: "",
    d0: null,
    d1: null,
    comment: "Full string — EMI + visual",
  });
  push({
    lane: "lane-inspections",
    cp: false,
    startH: 36,
    endH: 39,
    code1: "CALIBRATION",
    code2: "CAL_PVT",
    d0: null,
    d1: null,
  });
  push({
    lane: "lane-inspections",
    cp: false,
    startH: 39,
    endH: 41,
    code1: "CALIBRATION",
    code2: "CAL_HOOKLOAD",
    d0: null,
    d1: null,
  });

  milestone("lane-mob", MOB_HOURS - 2, "PRE_SPUD_CHECKLIST");
}

/** Run casing → cement → WOC → (BOP) → drill out → integrity test for one
 *  cemented string. Returns the hour the well is ready to drill ahead. */
function pushCasingCycle(
  push: (s: Spec) => void,
  args: {
    at: number;
    shoeMD: number;
    label: string;
    /** Only the surface string gets the BOP nippled up onto the wellhead. */
    nippleUpBop: boolean;
    /** Casing point is a gating milestone at every section TD. */
    integrity: "FIT" | "LOT" | "CSG_TEST";
    /** Whether the well is drilled AHEAD of this shoe. False for the final
     *  (production) string: you pressure-test it and complete, you do not drill
     *  its shoe track out and make hole below TD. */
    drillAhead: boolean;
  },
): number {
  const { at, shoeMD, label, nippleUpBop, integrity, drillAhead } = args;
  // `atMD` defaults to the shoe, but the integrity test happens AFTER the shoe
  // track is drilled out, so the hole is 5 m deeper by then. Pinning it to the
  // shoe put a 5 m step backwards into the days-vs-depth curve.
  const zonal = (
    startH: number,
    endH: number,
    code1: string,
    code2 = "",
    comment = "",
    atMD: number = shoeMD,
  ): void => {
    push({
      lane: "lane-zonal",
      cp: false,
      startH: h1(startH),
      endH: h1(endH),
      code1,
      code2,
      d0: Math.round(atMD),
      d1: Math.round(atMD),
      comment,
    });
  };

  // Running casing IS the critical path — nothing else progresses the well.
  const runH = RIGUP_CASING_CREW_H + pipeHours(shoeMD, RUN_CASING_FTHR);
  let t = at;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + runH),
    code1: "RUN_CASING",
    code2: "RUN_CSG",
    d0: Math.round(shoeMD),
    d1: Math.round(shoeMD),
    comment: `${label} — run to shoe`,
  });
  push({
    lane: "lane-inspections",
    cp: false,
    startH: h1(t),
    endH: h1(t + runH * 0.8),
    code1: "CASING_PREP",
    code2: "DRIFT",
    d0: null,
    d1: null,
    comment: "Strap / drift on the rack, ahead of the elevators",
  });
  t += runH;

  zonal(t, t + RIGUP_CEMENT_H, "RU_CEMENT");
  zonal(
    t + RIGUP_CEMENT_H,
    t + RIGUP_CEMENT_H + CEMENT_H,
    "CEMENT_JOB",
    "",
    `${label} — lead + tail`,
  );
  t += RIGUP_CEMENT_H + CEMENT_H;
  push({
    lane: "lane-zonal",
    cp: true,
    startH: h1(t),
    endH: h1(t),
    code1: "PLUG_BUMPED",
    code2: "",
    d0: Math.round(shoeMD),
    d1: Math.round(shoeMD),
    kind: "milestone",
  });
  zonal(t, t + WOC_H, "WOC");
  t += WOC_H;
  push({
    lane: "lane-zonal",
    cp: true,
    startH: h1(t),
    endH: h1(t),
    code1: "WOC_COMPLETE",
    code2: "",
    d0: Math.round(shoeMD),
    d1: Math.round(shoeMD),
    kind: "milestone",
  });

  if (nippleUpBop) {
    // Nippling up onto the wellhead is on the critical path; the stump test is
    // already done, this is the CASING test the regulator witnesses.
    push({
      lane: "lane-bop",
      cp: false,
      startH: h1(t),
      endH: h1(t + INSTALL_WELLHEAD_H + NU_TEST_BOP_H),
      code1: "CSG_NU",
      code2: "",
      d0: null,
      d1: null,
    });
    push({
      lane: "lane-bop",
      cp: true,
      startH: h1(t + 8),
      endH: h1(t + 8),
      code1: "BOP_NIPPLED_UP",
      code2: "",
      d0: null,
      d1: null,
      kind: "milestone",
    });
    push({
      lane: "lane-bop",
      cp: false,
      startH: h1(t + 8),
      endH: h1(t + 10),
      code1: "CSG_PT_LOW",
      code2: "",
      d0: null,
      d1: null,
    });
    push({
      lane: "lane-bop",
      cp: false,
      startH: h1(t + 10),
      endH: h1(t + 13),
      code1: "CSG_PT_HIGH",
      code2: "",
      d0: null,
      d1: null,
    });
    push({
      lane: "lane-bop",
      cp: false,
      startH: h1(t + 13),
      endH: h1(t + 14.5),
      code1: "CSG_ACTUATION",
      code2: "",
      d0: null,
      d1: null,
    });
    push({
      lane: "lane-bop",
      cp: true,
      startH: h1(t + 14.5),
      endH: h1(t + 14.5),
      code1: "CSG_TEST_WITNESSED",
      code2: "",
      d0: null,
      d1: null,
      kind: "milestone",
      comment: "Witnessed — RRC district office",
    });
    push({
      lane: "lane-inspections",
      cp: false,
      startH: h1(t + 14.5),
      endH: h1(t + 14.5),
      code1: "hse-safety",
      code2: "jsa",
      d0: null,
      d1: null,
      kind: "report",
      comment: "Pre-drill-ahead JSA",
    });
    t += 14.5;
  }

  // Drill out the shoe track and test the formation below it — only when the well
  // is drilled ahead of this shoe.
  if (!drillAhead) {
    zonal(t, t + FIT_H, "CSG_TEST", "", "Casing pressure test", shoeMD);
    return t + FIT_H;
  }
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + 3),
    code1: "DRILLING",
    code2: "DRILL_CEMENT",
    // HOLE depth, not BIT depth. This used to start at shoeMD − 25 m — where the
    // BIT re-enters to drill the shoe track — which put a 25 m DIP into the
    // days-vs-depth curve. That chart plots hole depth (the monotonic drilled
    // progress); the bit's position during a trip or a drill-out is not it.
    d0: Math.round(shoeMD),
    d1: Math.round(shoeMD + SHOE_TRACK_M),
    comment: "Drill out float / shoe track",
  });
  t += 3;
  zonal(
    t,
    t + FIT_H,
    integrity === "CSG_TEST" ? "CSG_TEST" : integrity,
    "",
    "Shoe integrity",
    shoeMD + SHOE_TRACK_M,
  );
  t += FIT_H;
  push({
    lane: "lane-zonal",
    cp: true,
    startH: h1(t),
    endH: h1(t),
    code1: "FIT_LOT_ACHIEVED",
    code2: "",
    d0: Math.round(shoeMD + SHOE_TRACK_M),
    d1: Math.round(shoeMD + SHOE_TRACK_M),
    kind: "milestone",
  });
  return t;
}

// ── The full spud-to-release sequence ───────────────────────────────────────

export interface Sequence {
  specs: Spec[];
  spudH: number;
  releaseH: number;
  /** Midpoint of the last lateral drilling block — where a live well's "now" is
   *  placed so the 32-hour window opens onto drilling, not onto a trip. */
  lateralMidH: number;
}

/** EXPORTED so the per-day demo generators (cost log, mud, drilling params) can bucket THIS
 *  sequence into calendar days rather than each rolling its own duration model. The PO's spec
 *  (DECISIONS.md 2026-09-03) is transcribed once, here; everything downstream is a VIEW of it.
 *  See demoWellProgram.ts. */
export function buildSequence(
  geom: OilfieldWellGeometry,
  previousWell: string,
): Sequence {
  const specs: Spec[] = [];
  const push = (s: Spec): void => {
    specs.push(s);
  };

  pushMobilization(push, previousWell);
  const spudH = MOB_HOURS;
  push({
    lane: "lane-critical",
    cp: true,
    startH: spudH,
    endH: spudH,
    code1: "SPUD",
    code2: "",
    d0: Math.round(geom.conductorShoeMD),
    d1: Math.round(geom.conductorShoeMD),
    kind: "milestone",
    comment: "Spud — drilling day 1 starts here, not at move-in",
  });

  let t = spudH;

  // ── SURFACE SECTION — 17-1/2" hole, conductor shoe → surface shoe ─────────
  // Isolates the fresh-water sand. Fast, unconsolidated, and drilled before the
  // BOP is on the wellhead (diverter only).
  push({
    lane: "lane-mwd",
    cp: false,
    startH: h1(t - 2),
    endH: h1(t),
    code1: "BHA_MAKEUP",
    code2: "",
    d0: null,
    d1: null,
    comment: "Vertical BHA — packed hole assembly",
  });
  t = drillInterval(push, {
    fromMD: geom.conductorShoeMD,
    toMD: geom.surfaceShoeMD,
    ropMhr: ROP_SURFACE,
    at: t,
    code2: "ROTARY",
    blockHours: 6,
    comment: 'Surface hole — 17-1/2"',
  });
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + CTCH_SURFACE_H),
    code1: "CIRC_COND",
    code2: "",
    d0: Math.round(geom.surfaceShoeMD),
    d1: Math.round(geom.surfaceShoeMD),
    comment: "Circulate bottoms up, condition for casing",
  });
  t += CTCH_SURFACE_H;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + pipeHours(geom.surfaceShoeMD, TOOH_FTHR)),
    code1: "TRIPS",
    code2: "POOH",
    d0: Math.round(geom.surfaceShoeMD),
    d1: Math.round(geom.surfaceShoeMD),
  });
  t += pipeHours(geom.surfaceShoeMD, TOOH_FTHR);
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t),
    code1: "CASING_POINT",
    code2: "",
    d0: Math.round(geom.surfaceShoeMD),
    d1: Math.round(geom.surfaceShoeMD),
    kind: "milestone",
    comment: 'Surface casing point — 13-3/8"',
  });
  t = pushCasingCycle(push, {
    at: t,
    shoeMD: geom.surfaceShoeMD,
    label: '13-3/8" surface',
    nippleUpBop: true,
    integrity: "FIT",
    drillAhead: true,
  });

  // ── INTERMEDIATE SECTION — 12-1/4" hole, surface shoe → just above KOP ────
  // ITS JOB IS THE SALT. Long vertical grind through the Salado; the wiper trip
  // and the tight-hole NPT below are both salt creep, which is what this string
  // is run to stop.
  push({
    lane: "lane-mwd",
    cp: false,
    startH: h1(t),
    endH: h1(t + BHA_MAKEUP_H),
    code1: "BHA_MAKEUP",
    code2: "",
    d0: null,
    d1: null,
  });
  const intMid =
    (geom.surfaceShoeMD + SHOE_TRACK_M + geom.intermediateShoeMD) / 2;
  t = drillInterval(push, {
    fromMD: geom.surfaceShoeMD + SHOE_TRACK_M,
    toMD: intMid,
    ropMhr: ROP_INTERMEDIATE,
    at: t,
    code2: "ROTARY",
    blockHours: 12,
    comment: 'Intermediate hole — 12-1/4" into the Salado',
  });
  // Salt creep: hole tightens, wiper trip, then reaming back to bottom. Depth is
  // FLAT through the NPT — the flat spot under the red block on the depth curve.
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + 4),
    code1: "TRIPS",
    code2: "WIPER_TRIP",
    d0: Math.round(intMid),
    d1: Math.round(intMid),
    comment: "Wiper trip — salt creep, hole tightening up",
  });
  t += 4;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + 7),
    code1: "NPT",
    code2: "WELLBORE",
    d0: Math.round(intMid),
    d1: Math.round(intMid),
    party: "Operator",
    comment: "Tight hole through the Salado — reaming back to bottom",
  });
  t += 7;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + 3),
    code1: "REAMING",
    code2: "",
    d0: Math.round(intMid),
    d1: Math.round(intMid),
  });
  t += 3;
  t = drillInterval(push, {
    fromMD: intMid,
    toMD: geom.intermediateShoeMD,
    ropMhr: ROP_INTERMEDIATE,
    at: t,
    code2: "ROTARY",
    blockHours: 12,
    comment: "Drilling ahead to the intermediate shoe",
  });
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + CTCH_INTERMEDIATE_H + CONDITION_MUD_H),
    code1: "CIRC_COND",
    code2: "",
    d0: Math.round(geom.intermediateShoeMD),
    comment: "CTCH end of intermediate + condition mud",
    d1: Math.round(geom.intermediateShoeMD),
  });
  t += CTCH_INTERMEDIATE_H + CONDITION_MUD_H;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + pipeHours(geom.intermediateShoeMD, TOOH_FTHR)),
    code1: "TRIPS",
    code2: "POOH",
    d0: Math.round(geom.intermediateShoeMD),
    d1: Math.round(geom.intermediateShoeMD),
  });
  t += pipeHours(geom.intermediateShoeMD, TOOH_FTHR);
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t),
    code1: "CASING_POINT",
    code2: "",
    d0: Math.round(geom.intermediateShoeMD),
    d1: Math.round(geom.intermediateShoeMD),
    kind: "milestone",
    comment: 'Intermediate casing point — 9-5/8", salt cased off',
  });
  t = pushCasingCycle(push, {
    at: t,
    shoeMD: geom.intermediateShoeMD,
    label: '9-5/8" intermediate',
    nippleUpBop: false,
    integrity: "LOT",
    drillAhead: true,
  });

  // ── PRODUCTION SECTION — 8-1/2" hole, curve then the two-mile lateral ─────
  push({
    lane: "lane-mwd",
    cp: false,
    startH: h1(t),
    endH: h1(t + BHA_MAKEUP_H),
    code1: "BHA_MAKEUP",
    code2: "",
    d0: null,
    d1: null,
    comment: "Directional BHA — RSS + MWD/LWD",
  });
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + pipeHours(geom.intermediateShoeMD, TIH_FTHR)),
    code1: "TRIPS",
    code2: "RIH_BHA",
    // Post-drill-out: the hole is already SHOE_TRACK_M below the shoe.
    d0: Math.round(geom.intermediateShoeMD + SHOE_TRACK_M),
    d1: Math.round(geom.intermediateShoeMD + SHOE_TRACK_M),
    comment: "TIH for curve",
  });
  t += pipeHours(geom.intermediateShoeMD, TIH_FTHR);
  // Kick off, then build to 90°.
  t = drillInterval(push, {
    fromMD: geom.intermediateShoeMD + SHOE_TRACK_M,
    toMD: geom.landingMD,
    ropMhr: ROP_CURVE,
    at: t,
    code2: "SLIDE",
    blockHours: 10,
    comment: "Curve — kick off and build to 90°",
  });
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + 2),
    code1: "CIRC_COND",
    code2: "",
    d0: Math.round(geom.landingMD),
    d1: Math.round(geom.landingMD),
    comment: "Landed in Wolfcamp A — sweep before the lateral",
  });
  t += 2;
  push({
    lane: "lane-mwd",
    cp: false,
    startH: h1(t),
    endH: h1(t + 0.6),
    code1: "SURVEY",
    code2: "",
    d0: Math.round(geom.landingMD),
    d1: Math.round(geom.landingMD),
    comment: "Landing point survey — TVD confirmed against the plan",
  });
  // The lateral, split either side of a motor-failure NPT + trip.
  const latMid = geom.landingMD + (geom.totalMD - geom.landingMD) * 0.55;
  t = drillInterval(push, {
    fromMD: geom.landingMD,
    toMD: latMid,
    ropMhr: ROP_LATERAL,
    at: t,
    code2: "RSS",
    blockHours: 12,
    comment: "Lateral — rotary steerable, holding 90° in zone",
  });
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + RIG_SERVICE_RANDOM_H),
    code1: "SVC_RIG",
    code2: "",
    d0: Math.round(latMid),
    d1: Math.round(latMid),
    party: "Drilling Contractor",
    // PO: "Can include random rig service of 12-24 hours which would be for pumps,
    // cut and slip, etc." This replaced an agent-invented "MWD telemetry loss" NPT.
    comment: "Rig service — pumps, cut and slip",
  });
  t += RIG_SERVICE_RANDOM_H;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + pipeHours(latMid, TOOH_FTHR)),
    code1: "TRIPS",
    code2: "POOH_BHA",
    d0: Math.round(latMid),
    d1: Math.round(latMid),
    comment: "POOH from the lateral — pumping out through the curve",
  });
  t += pipeHours(latMid, TOOH_FTHR);
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + pipeHours(latMid, TIH_FTHR)),
    code1: "TRIPS",
    code2: "RIH_BHA",
    d0: Math.round(latMid),
    d1: Math.round(latMid),
  });
  t += pipeHours(latMid, TIH_FTHR);
  const lateralRunStartH = t;
  t = drillInterval(push, {
    fromMD: latMid,
    toMD: geom.totalMD,
    ropMhr: ROP_LATERAL,
    at: t,
    code2: "RSS",
    blockHours: 12,
    comment: "Drilling ahead to TD",
  });
  const lateralMidH = (lateralRunStartH + t) / 2;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t),
    code1: "TD_REACHED",
    code2: "",
    d0: Math.round(geom.totalMD),
    d1: Math.round(geom.totalMD),
    kind: "milestone",
    comment: "TD — total depth reached",
  });
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + 6),
    code1: "CIRC_COND",
    code2: "",
    d0: Math.round(geom.totalMD),
    d1: Math.round(geom.totalMD),
    comment: "Circulate hole clean — two-mile lateral, multiple bottoms up",
  });
  t += 6;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + 4),
    code1: "TRIPS",
    code2: "WIPER_TRIP",
    d0: Math.round(geom.totalMD),
    d1: Math.round(geom.totalMD),
  });
  t += 4;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + pipeHours(geom.totalMD, TOOH_FTHR)),
    code1: "TRIPS",
    code2: "POOH",
    d0: Math.round(geom.totalMD),
    d1: Math.round(geom.totalMD),
  });
  t += pipeHours(geom.totalMD, TOOH_FTHR);
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t),
    code1: "CASING_POINT",
    code2: "",
    d0: Math.round(geom.totalMD),
    d1: Math.round(geom.totalMD),
    kind: "milestone",
    comment: 'Production casing point — 5-1/2" to TD',
  });
  t = pushCasingCycle(push, {
    at: t,
    shoeMD: geom.totalMD,
    label: '5-1/2" production',
    nippleUpBop: false,
    integrity: "CSG_TEST",
    drillAhead: false,
  });

  // Cement evaluation, then rig down and release.
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t + 10),
    code1: "WIRELINE_LOGS",
    code2: "CBL_VDL",
    // Logging does NOT advance the hole — depth stays at TD. This used to run
    // totalMD → intermediateShoeMD, the TOOL's travel up the hole, which drew a
    // ~3,400 m spike at the end of the days-vs-depth curve.
    d0: Math.round(geom.totalMD),
    d1: Math.round(geom.totalMD),
    comment: "Cement bond log — verify isolation across the lateral",
  });
  push({
    lane: "lane-critical",
    cp: false,
    startH: h1(t + 10),
    endH: h1(t + 10),
    code1: "cement",
    code2: "cbl",
    d0: Math.round(geom.totalMD),
    d1: Math.round(geom.totalMD),
    kind: "report",
    comment: "CBL/VDL evaluation",
  });
  t += 10;
  push({
    lane: "lane-mob",
    cp: false,
    startH: h1(t),
    endH: h1(t + RIG_DOWN_HOURS),
    code1: "RIG_DOWN",
    // "rigdown BOP, install flange and cap. lay down drillpipe. rig down (1.5 days)"
    code2: "",
    d0: null,
    d1: null,
    comment: "Rig down BOP, flange and cap, lay down drillpipe",
  });
  t += RIG_DOWN_HOURS;
  push({
    lane: "lane-critical",
    cp: true,
    startH: h1(t),
    endH: h1(t),
    code1: "RIG_RELEASE",
    code2: "",
    d0: Math.round(geom.totalMD),
    d1: Math.round(geom.totalMD),
    kind: "milestone",
    comment: "Rig released",
  });

  // PO: "can include rig service 1-2 hours per day". Emitted on the Inspections lane
  // so it reads as routine upkeep running alongside the spine rather than stopping it;
  // the 12-24 h random service above is the one that IS on the critical path.
  for (let d = 1; spudH + d * 24 < t; d++) {
    const at = spudH + d * 24 + jitter(geom.totalMD + d, 5, 9);
    const dur = RIG_SERVICE_DAILY_H * jitter(geom.totalMD + d * 7, 0.7, 1.4);
    push({
      lane: "lane-inspections",
      cp: false,
      startH: h1(at),
      endH: h1(at + dur),
      code1: "RIG_INSP",
      code2: "",
      d0: null,
      d1: null,
      comment: d === 1 ? "Daily rig service" : "",
    });
  }

  return { specs, spudH, releaseH: h1(t), lateralMidH };
}

// ── Weather stream ──────────────────────────────────────────────────────────

interface WxSpec {
  h: number;
  precip: "none" | "rain" | "snow" | "sleet" | "hail";
  amt: string;
  temp: string;
  wind: string;
  hum: string;
  lightning: boolean;
  ldist: string;
  comment: string;
}

/** A West Texas weather stream spread across the job — sampled roughly twice a
 *  day, with one lightning cell (the Permian summer pattern that stands the crew
 *  down off the derrick). */
function weatherSpecs(releaseH: number): WxSpec[] {
  const out: WxSpec[] = [];
  const step = 12;
  for (let h = 6; h < releaseH; h += step) {
    const day = Math.floor(h / 24);
    const isNight = h % 24 > 18 || h % 24 < 6;
    const lightningCell = day % 7 === 3 && !isNight;
    out.push({
      h,
      precip: lightningCell ? "rain" : "none",
      amt: lightningCell ? "0.4" : "",
      temp: isNight ? String(58 + (day % 5)) : String(88 + (day % 9)),
      wind: String(9 + ((day * 3) % 16)),
      hum: isNight ? String(48 + (day % 12)) : String(21 + (day % 15)),
      lightning: lightningCell,
      ldist: lightningCell ? "4" : "",
      comment: lightningCell
        ? "Thunderstorm cell — crew down off the derrick"
        : "",
    });
  }
  return out;
}

// ── Public builder ──────────────────────────────────────────────────────────

export interface OilfieldTimeLogArgs {
  ids: { padId: string; wellboreId: string; jobId: string };
  geometry: OilfieldWellGeometry;
  phase: OilfieldWellPhase;
  /** ms since epoch for "now" — injected so the builder is deterministic in tests. */
  nowMs: number;
  /** Rig-campaign position: 0 = the well being drilled now, +n = n wells earlier
   *  in the sequence (finished n cycles ago), −n = n wells later (not yet spudded). */
  campaignOffset: number;
  /** Name of the well the rig walked off, for the move-in comment. */
  previousWell: string;
  /** EXPLICIT move-in instant (epoch ms), overriding the `nowMs` + `campaignOffset` derivation.
   *
   *  WHY THIS EXISTS. The derivation below places the pad relative to `Date.now()` so that a live
   *  well's 32-hour window opens onto drilling. The PO has since ruled the pad HISTORICAL — drilled
   *  in 2018, with a workover as the live job (DECISIONS.md 2026-09-03) — and a fixed calendar
   *  anchor cannot be expressed as an offset from a moving `now`. The objection that motivated the
   *  `nowMs` anchoring no longer applies either: `useTimeline.focusWindowStart` re-anchors the
   *  window on the JOB, so a 2018 log opens on its own last day rather than on empty space.
   *
   *  When set, `campaignOffset` is not used for placement (it still selects nothing else), and the
   *  caller owns the campaign walk. */
  originMs?: number;
}

export interface OilfieldTimeLogResult {
  timeLog: JobTimeLog;
  /** Job header datetimes, derived from the log so they cannot drift from it. */
  spudDate: string;
  startDate: string;
  endDate: string | null;
}

export function generateOilfieldTimeLog(
  args: OilfieldTimeLogArgs,
): OilfieldTimeLogResult {
  const { ids, geometry, phase, nowMs, campaignOffset, previousWell } = args;
  const { specs, spudH, releaseH, lateralMidH } = buildSequence(
    geometry,
    previousWell,
  );

  // ── Anchoring ────────────────────────────────────────────────────────────
  // The rig drills the pad SEQUENTIALLY — one well at a time — so exactly one
  // well is live at any instant. `campaignOffset` places each well against that
  // single rig clock, and the well currently drilling is anchored so that `now`
  // falls MID-LATERAL: the 32-hour default window then opens onto live drilling
  // instead of empty space.
  const cycleH = releaseH + 6; // release → next well's move-in
  const originMs =
    args.originMs ??
    nowMs - (lateralMidH + campaignOffset * cycleH) * MS_PER_HOUR;

  const iso = (hours: number): string =>
    toOffsetISO(new Date(originMs + hours * MS_PER_HOUR));
  const stamp = toOffsetISO(new Date(nowMs));
  const nowH = (nowMs - originMs) / MS_PER_HOUR;

  /** An entry is PLANNED when it has not started yet — the look-ahead half of a
   *  live job's timeline. A completed well has none; a not-yet-spudded well is
   *  entirely planned. */
  const isPlanned = (startH: number): boolean => {
    if (phase === "completed") return false;
    if (phase === "planned") return true;
    return startH > nowH;
  };

  const entries: TimeEntry[] = specs.map((s, i) => ({
    id: `${ids.jobId}-te-${i}`,
    padId: ids.padId,
    wellboreId: ids.wellboreId,
    jobId: ids.jobId,
    laneId: s.lane,
    onCriticalPath: s.cp,
    entryKind: s.kind ?? "activity",
    startTs: iso(s.startH),
    endTs: iso(s.endH),
    code1: s.code1,
    code2: s.code2,
    depthStart: s.d0 === null ? "" : String(s.d0),
    depthStartUnit: "m",
    depthEnd: s.d1 === null ? "" : String(s.d1),
    depthEndUnit: "m",
    party: s.party ?? "",
    contractor: "",
    sharedResourceId: null,
    isPlanned: isPlanned(s.startH),
    codingTemplateId: CODING_TEMPLATE_ID,
    codingTemplateVersion: CODING_TEMPLATE_VERSION,
    comment: s.comment ?? "",
    attachments: [],
    createdAt: stamp,
    createdBy: "demo-generator",
    modifiedAt: stamp,
    modifiedBy: "demo-generator",
  }));

  // A well that has not spudded has no observed weather yet.
  const weather: WeatherObservation[] =
    phase === "planned"
      ? []
      : weatherSpecs(
          phase === "drilling" ? Math.min(releaseH, nowH) : releaseH,
        ).map((w, i) => ({
          id: `${ids.jobId}-wx-${i}`,
          padId: ids.padId,
          wellboreId: ids.wellboreId,
          jobId: ids.jobId,
          ts: iso(w.h),
          precipType: w.precip,
          precipAmount: w.amt,
          precipAmountUnit: "in",
          temperature: w.temp,
          temperatureUnit: "F",
          windspeed: w.wind,
          windspeedUnit: "mph",
          humidity: w.hum,
          baroPressure: "",
          baroPressureUnit: "inHg",
          lightning: w.lightning,
          lightningDistance: w.ldist,
          lightningDistanceUnit: "mi",
          naturalDisaster: "none",
          comment: w.comment,
          createdAt: stamp,
          createdBy: "demo-generator",
          modifiedAt: stamp,
          modifiedBy: "demo-generator",
        }));

  return {
    timeLog: { entries, lanes: [...DEFAULT_DRILLING_LANES], weather },
    spudDate: iso(spudH),
    startDate: iso(0),
    endDate: phase === "completed" ? iso(releaseH) : null,
  };
}
